/**
 * Website analytics: the API's JSON shapes, formatters and URL state.
 *
 * Ported from Sonar (src/lib/format.ts, src/lib/filters.ts). The page keeps
 * its whole view in the URL (`?client_id=&site=&range=&f=dim:value`), so a
 * view can be shared and the back button undoes a filter.
 *
 * Kept free of React and imports so node:test can load it directly
 * (see test/web-analytics-ui.test.js).
 */

// ── API shapes (routes/webAnalytics.js) ──────────────────────────────────

export type Bucket = "hour" | "day";

export type SiteSettings = {
  id: number;
  label: string | null;
  domains: string[];
  time_zone: string;
  money_paths: string[];
  hidden: boolean;
  monitor: boolean;
  monitor_url: string | null;
  conversion_events: string[];
  last_event_at: string | null;
};

/** One row of GET /api/web/:client_id/sites. */
export type Site = SiteSettings & {
  visitors_7d: number;
  live: number;
  /** Exactly 7 daily visitor counts, oldest first. */
  spark: number[];
};

/**
 * The sites list with one site replaced by the row PATCH /api/web/sites/:id
 * returned, so the settings dialog reopens on what was saved, not on the list
 * the reload has not replaced yet. That row has no counts; the old ones stay
 * until the reload brings new ones.
 */
export function withSavedSite(list: Site[] | null, saved: SiteSettings): Site[] | null {
  return list && list.map((s) => (s.id === saved.id ? { ...s, ...saved } : s));
}

export type Totals = { visitors: number; pageviews: number; sessions: number; bounces: number; visit_seconds: number };

/** `bucket` is a naive timestamp in the site's own zone, e.g. "2026-09-17T00:00:00". */
export type TimeseriesPoint = { bucket: string; visitors: number; pageviews: number };

export type BreakdownRow = {
  value: string;
  visitors: number;
  pageviews: number;
  sessions: number;
  bounces: number;
  dwell_seconds: number;
  dwell_samples: number;
};

export type Stats = {
  totals: Totals;
  timeseries: TimeseriesPoint[];
  breakdowns: Record<string, BreakdownRow[]>;
  first_event_at: string | null;
  site: SiteSettings;
  range: { from: string; to: string; bucket: Bucket; tz: string };
};

export type Breakdown = { rows: BreakdownRow[]; has_more: boolean };

export type Session = {
  session_key: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  pageviews: number;
  entry_path: string | null;
  exit_path: string | null;
  ref: string | null;
  src: string | null;
  medium: string | null;
  campaign: string | null;
  country: string | null;
  device: string | null;
  browser: string | null;
  os: string | null;
  is_money: boolean;
  is_engaged: boolean;
  /** The entry pageview's ad network (google, meta, …), or null. */
  click_source: string | null;
};

export type Live = { live: number };

// ── Formatters ────────────────────────────────────────────────────────────

const intFormatter = new Intl.NumberFormat("en-US");

/** 1,234 — every count shown at full precision. */
export function fmtInt(n: number): string {
  return intFormatter.format(n);
}

/** 42 → "42s", 125 → "2m 05s", 3900 → "1h 05m". Rounded first; negatives clamp to 0. */
export function fmtDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}

/** A fraction in, a whole percent out. `null` (a zero denominator) is "—", never "0%". */
export function fmtPct(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

const ratio = (n: number, d: number) => (d > 0 ? n / d : null);

export type Derived = { viewsPerVisit: number | null; bounceRate: number | null; avgVisitSeconds: number | null };

/** A visit is a session, as in Plausible's "views per visit". */
export function derive(t: Totals): Derived {
  return {
    viewsPerVisit: ratio(t.pageviews, t.sessions),
    bounceRate: ratio(t.bounces, t.sessions),
    avgVisitSeconds: ratio(t.visit_seconds, t.sessions),
  };
}

// ── Filters ───────────────────────────────────────────────────────────────

export const DIMS = [
  "path", "ref", "src", "medium", "campaign", "utm_content", "utm_term",
  "click_source", "country", "device", "browser", "os", "money", "engaged",
] as const;

export type Filter = { dim: string; value: string };

const DIM_LABELS: Record<string, string> = {
  path: "Page",
  ref: "Referrer",
  src: "Source",
  medium: "Medium",
  campaign: "Campaign",
  // The ad-id macro lands in utm_content, so what a reader sees there IS an ad.
  utm_content: "Ad",
  utm_term: "Keyword",
  click_source: "Click source",
  country: "Country",
  device: "Device",
  browser: "Browser",
  os: "OS",
  money: "Money",
  engaged: "Engaged",
};

export const isKnownDim = (dim: string) => (DIMS as readonly string[]).includes(dim);

/** A dim's name on its own; an unknown dim reads as itself. */
export function dimLabel(dim: string): string {
  return DIM_LABELS[dim] ?? dim;
}

/** What a chip says for one filter. */
export function filterLabel(f: Filter): string {
  if (f.dim === "money") return f.value === "1" ? "Money sessions" : f.value === "0" ? "Non-money sessions" : `Money is ${f.value}`;
  if (f.dim === "engaged") return f.value === "1" ? "Engaged sessions" : f.value === "0" ? "Not engaged" : `Engaged is ${f.value}`;
  return `${dimLabel(f.dim)} is ${f.value}`;
}

/** Elides the middle, so two long paths that share a prefix still read differently. */
export function truncateMiddle(value: string, max: number): string {
  if (max < 3 || value.length <= max) return value;
  const head = Math.ceil((max - 1) / 2);
  const tail = max - 1 - head;
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}

export const FILTER_CHIP_MAX_VALUE = 42;

/** Same cap as the API's parseFilters. */
const MAX_FILTERS = 12;

/**
 * `?f=dim:value`, split on the FIRST colon (`path:/a:b` is the path "/a:b").
 * One filter per dim: a later value replaces an earlier one in place. An entry
 * with no colon or no dim is dropped. An unknown dim is KEPT, as the API keeps
 * it: it matches nothing there, and the chip says so here.
 */
export function filtersFromSearch(search: URLSearchParams): Filter[] {
  const byDim = new Map<string, string>();
  for (const entry of search.getAll("f")) {
    const i = entry.indexOf(":");
    if (i <= 0) continue;
    const dim = entry.slice(0, i);
    if (!byDim.has(dim) && byDim.size >= MAX_FILTERS) continue;
    byDim.set(dim, entry.slice(i + 1));
  }
  return [...byDim].map(([dim, value]) => ({ dim, value }));
}

function writeFilters(search: URLSearchParams, filters: Filter[]): string {
  const next = new URLSearchParams(search);
  next.delete("f");
  for (const f of filters) next.append("f", `${f.dim}:${f.value}`);
  return next.toString();
}

/** The search string with `dim` set to `value`, replacing any earlier value for it. */
export function withFilter(search: URLSearchParams, dim: string, value: string): string {
  const filters = filtersFromSearch(search);
  const i = filters.findIndex((f) => f.dim === dim);
  if (i === -1) filters.push({ dim, value });
  else filters[i] = { dim, value };
  return writeFilters(search, filters);
}

/** The search string without `dim`'s filter; `null` clears every filter. */
export function withoutFilter(search: URLSearchParams, dim: string | null): string {
  return writeFilters(search, dim === null ? [] : filtersFromSearch(search).filter((f) => f.dim !== dim));
}

/** The search string with one plain param set (or removed with `null`). */
export function withParams(search: URLSearchParams, values: Record<string, string | null>): string {
  const next = new URLSearchParams(search);
  for (const [key, value] of Object.entries(values)) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  return next.toString();
}

// ── Range ─────────────────────────────────────────────────────────────────

export const RANGES = ["today", "7d", "30d", "90d", "this_week", "last_week", "mtd", "last_month"] as const;
export type Preset = (typeof RANGES)[number];
/** Calendar presets: sent as the exact days, which the page works out in the site's zone (date-range.ts). */
export const CALENDAR_RANGES: readonly Preset[] = ["this_week", "last_week", "mtd", "last_month"];
export type RangeState = { range: Preset } | { range: "custom"; from: string; to: string };

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (v: string | null): v is string => !!v && YMD.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));

/** The range the URL asks for. Junk degrades to 7 days, never to an error. */
export function readRange(search: URLSearchParams): RangeState {
  const range = search.get("range");
  if (range === "custom") {
    const from = search.get("from");
    const to = search.get("to");
    if (validDate(from) && validDate(to) && from <= to) return { range: "custom", from, to };
    return { range: "7d" };
  }
  return (RANGES as readonly string[]).includes(range ?? "") ? { range: range as Preset } : { range: "7d" };
}

/** Minutes `zone` is ahead of UTC at instant `ms`. */
function offsetMinutes(ms: number, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

/**
 * 00:00 on `ymd` in `zone`, as an instant. Two passes, because the offset at
 * the first guess can differ from the offset at the answer on a DST day.
 */
export function zonedMidnight(ymd: string, zone: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - offsetMinutes(guess, zone) * 60000;
  return new Date(guess - offsetMinutes(first, zone) * 60000);
}

const nextDay = (ymd: string) => new Date(Date.parse(`${ymd}T00:00:00Z`) + 86400e3).toISOString().slice(0, 10);

/**
 * The query string for /stats, /breakdown, /sessions: site, range and filters
 * from the page URL. A custom range's dates are calendar days in the site's
 * zone, and the end date is inclusive (to = the next day's midnight). A
 * calendar preset (month to date, last week) goes as the custom days that
 * `calendarDays` gives for it.
 */
export function statsQuery(search: URLSearchParams, zone: string, calendarDays?: (range: Preset) => { from: string; to: string }): string {
  const q = new URLSearchParams();
  const site = search.get("site");
  if (site) q.set("site", site);
  const r = readRange(search);
  const days = r.range === "custom" ? r : calendarDays && CALENDAR_RANGES.includes(r.range) ? calendarDays(r.range) : null;
  q.set("range", days ? "custom" : CALENDAR_RANGES.includes(r.range as Preset) ? "7d" : r.range);
  if (days) {
    q.set("from", zonedMidnight(days.from, zone).toISOString());
    q.set("to", zonedMidnight(nextDay(days.to), zone).toISOString());
  }
  for (const f of filtersFromSearch(search)) q.append("f", `${f.dim}:${f.value}`);
  return q.toString();
}

// ── Chart ─────────────────────────────────────────────────────────────────

/** The current hour in `zone`, in the API's naive bucket format. */
function zonedHourKey(now: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:00:00`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Hours as "1 PM", days as "Sep 3". Read straight off the naive bucket, so no zone math. */
export function bucketLabel(bucket: string, kind: Bucket): string {
  const [date, time = "00"] = bucket.split("T");
  const [, month, day] = date.split("-").map(Number);
  if (kind === "day") return `${MONTHS[month - 1]} ${day}`;
  const hour = Number(time.slice(0, 2));
  return `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "AM" : "PM"}`;
}

const HOUR = 3600e3;
/** Steps a naive "YYYY-MM-DDTHH:00:00" key by `ms`, treating it as UTC so no zone shifts it. */
const stepKey = (key: string, ms: number) => new Date(Date.parse(`${key}Z`) + ms).toISOString().slice(0, 19);

/**
 * The chart's series: every bucket from the range's start up to now, with the
 * hours or days that had no events filled in as zeros (the API returns only
 * buckets that have events), and nothing after the current hour.
 */
export function fillSeries(points: TimeseriesPoint[], range: Stats["range"], now: Date = new Date()): TimeseriesPoint[] {
  const byKey = new Map(points.map((p) => [p.bucket.slice(0, 19), p]));
  const nowKey = zonedHourKey(now, range.tz);
  const day = range.bucket === "day";
  const snap = (key: string) => (day ? `${key.slice(0, 10)}T00:00:00` : key);
  const endKey = snap(zonedHourKey(new Date(range.to), range.tz));
  const keys = new Set<string>();
  // Bounded: the API caps a window at 400 days, and hourly buckets at 48 hours.
  for (let k = snap(zonedHourKey(new Date(range.from), range.tz)), n = 0; k < endKey && k <= nowKey && n < 1200; k = stepKey(k, day ? 24 * HOUR : HOUR), n++) keys.add(k);
  for (const k of byKey.keys()) if (k <= nowKey) keys.add(k);
  return [...keys].sort().map((k) => ({ bucket: k, visitors: byKey.get(k)?.visitors ?? 0, pageviews: byKey.get(k)?.pageviews ?? 0 }));
}

// ── Breakdowns and the map ────────────────────────────────────────────────

/** Rows per dim that /stats returns; a panel with this many may have more behind "Show more". */
export const BREAKDOWN_PAGE = 10;

/** A row's bar, as a percent of the panel's top row (Sonar scales to the max, not the total). */
export function barPct(visitors: number, max: number): number {
  if (max <= 0) return visitors > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, (visitors / max) * 100));
}

/**
 * How much ink a country on the map gets, 22–100%. Square root, as in Sonar,
 * so one big country does not wash every other one out to the floor.
 */
export function mapShare(visitors: number, max: number): number {
  const f = max > 0 ? Math.min(1, Math.max(0, visitors / max)) : 1;
  return Math.round((22 + 78 * Math.sqrt(f)) * 10) / 10;
}

/** A country's fill. No visits is the bare track, so "some" and "none" never blur together. */
export function mapFill(visitors: number | undefined, max: number): string {
  if (!visitors || visitors <= 0) return "var(--chart-track)";
  return `color-mix(in srgb, var(--chart-ink) ${mapShare(visitors, max)}%, var(--chart-track))`;
}

/** The next page of a breakdown after the rows already shown. A value is never listed twice. */
export function mergeRows(shown: BreakdownRow[], next: BreakdownRow[]): BreakdownRow[] {
  const seen = new Set(shown.map((r) => r.value));
  return [...shown, ...next.filter((r) => !seen.has(r.value))];
}

let regionNames: Intl.DisplayNames | null | undefined;

/** "US" → "United States". Anything the browser cannot name reads as its code. */
export function countryName(code: string): string {
  if (regionNames === undefined) {
    try { regionNames = new Intl.DisplayNames(["en"], { type: "region" }); } catch { regionNames = null; }
  }
  if (!regionNames || !/^[A-Za-z]{2}$/.test(code)) return code.toUpperCase();
  try { return regionNames.of(code.toUpperCase()) ?? code.toUpperCase(); } catch { return code.toUpperCase(); }
}

/** What a breakdown row says for its value. */
export function rowLabel(dim: string, value: string): string {
  if (value === "") return "(empty)";
  return dim === "country" ? countryName(value) : value;
}

/** A row click: filter by the value, or remove the filter when that value is the one already picked. */
export function pickFilter(search: URLSearchParams, dim: string, value: string): string {
  const active = filtersFromSearch(search).find((f) => f.dim === dim);
  return active && active.value === value ? withoutFilter(search, dim) : withFilter(search, dim, value);
}

/** "Show more" pages a panel has loaded, for the one query (`key`) they were fetched with. */
export type MorePages = { key: string; byDim: Record<string, { rows: BreakdownRow[]; hasMore: boolean }> };

/** The loaded pages for `dim`, only if they belong to the query now on screen. */
export function morePages(state: MorePages, key: string, dim: string) {
  return state.key === key ? state.byDim[dim] : undefined;
}

/**
 * Folds a "Show more" response in. It is dropped when the page's query changed
 * while it was in flight, or while new stats are still loading (the rows on
 * screen then belong to the previous query, and its offset means nothing).
 */
export function applyMore(
  state: MorePages,
  req: { key: string; dim: string; shown: BreakdownRow[] },
  res: Breakdown,
  current: { key: string; busy: boolean },
): MorePages {
  if (current.busy || req.key !== current.key) return state;
  const byDim = state.key === req.key ? state.byDim : {};
  return { key: req.key, byDim: { ...byDim, [req.dim]: { rows: mergeRows(req.shown, res.rows), hasMore: res.has_more } } };
}

// ── Signals ───────────────────────────────────────────────────────────────

/** Mirrors the API's engaged thresholds (routes/webAnalytics.js ENGAGED_PV / ENGAGED_SECS). */
export const ENGAGED_MIN_PAGEVIEWS = 3;
export const ENGAGED_MIN_SECONDS = 120;

export type Signal = { dim: "money" | "engaged"; visitors: number; share: number | null; active: boolean };

/**
 * Money-page or engaged VISITORS (visitor_hash), read off the stats payload's
 * own `money` / `engaged` breakdown: the "1" row's visitors. That breakdown
 * ignores a filter on its own dim, so while the dim is unfiltered it sees the
 * same visits as `totals` and the share is exact. Once the dim IS filtered,
 * `totals` only holds the filtered visitors, so there is no honest share.
 */
export function signal(stats: Pick<Stats, "totals" | "breakdowns">, filters: Filter[], dim: "money" | "engaged"): Signal {
  const visitors = stats.breakdowns[dim]?.find((r) => r.value === "1")?.visitors ?? 0;
  const own = filters.find((f) => f.dim === dim);
  return {
    dim,
    visitors,
    share: own ? null : ratio(visitors, stats.totals.visitors),
    active: own?.value === "1",
  };
}

// ── Sessions ──────────────────────────────────────────────────────────────

/** "/ → /quote", or one path when the visit never left its first page. */
export function sessionPath(s: Pick<Session, "entry_path" | "exit_path" | "pageviews">): string {
  const entry = s.entry_path ?? "—";
  if (s.pageviews <= 1 || !s.exit_path || s.exit_path === s.entry_path) return entry;
  return `${entry} → ${s.exit_path}`;
}

/** Where the visit came from: the referring site, else the UTM source, else Direct. */
export function sessionSource(s: Pick<Session, "ref" | "src">): string {
  return s.ref || s.src || "Direct";
}

const CLICK_NAMES: Record<string, string> = {
  google: "Google", meta: "Meta", tiktok: "TikTok", bing: "Microsoft", linkedin: "LinkedIn",
  x: "X", pinterest: "Pinterest", snapchat: "Snapchat",
};

export type BadgeKind = "money" | "engaged" | "bounce" | "click";
export type Badge = { kind: BadgeKind; label: string; title: string };

/**
 * A session's badges, in a fixed order. Bounce is a one-page visit (the scan's
 * is_bounce), so it never sits beside Engaged-by-pages, only beside
 * Engaged-by-time on a long single page.
 */
export function sessionBadges(s: Pick<Session, "is_money" | "is_engaged" | "pageviews" | "click_source">): Badge[] {
  const out: Badge[] = [];
  if (s.is_money) out.push({ kind: "money", label: "Money", title: "Visited a money page" });
  if (s.is_engaged) out.push({ kind: "engaged", label: "Engaged", title: `${ENGAGED_MIN_PAGEVIEWS}+ pages or ${ENGAGED_MIN_SECONDS / 60}+ minutes` });
  if (s.pageviews === 1) out.push({ kind: "bounce", label: "Bounce", title: "Left after one page" });
  if (s.click_source) {
    const name = CLICK_NAMES[s.click_source] ?? s.click_source;
    out.push({ kind: "click", label: `${name} ad`, title: `Arrived from a ${name} ad click` });
  }
  return out;
}

// ── Site settings ─────────────────────────────────────────────────────────

/** The API's caps (routes/webAnalytics.js). */
export const MONEY_PATHS_MAX = 20;
export const MONEY_PATH_MAX_LENGTH = 200;
export const CONVERSION_EVENTS_MAX = 20;
export const CONVERSION_EVENT_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;

export const DEFAULT_CONVERSION_EVENTS = [
  { name: "lead", label: "Lead form" },
  { name: "call_click", label: "Call click" },
  { name: "contact_email", label: "Email click" },
  { name: "contact_whatsapp", label: "WhatsApp click" },
  { name: "job_booked", label: "Job booked" },
  { name: "job_completed", label: "Job completed" },
] as const;

export const US_TIME_ZONES = [
  { id: "America/New_York", label: "Eastern" },
  { id: "America/Chicago", label: "Central" },
  { id: "America/Denver", label: "Mountain" },
  { id: "America/Phoenix", label: "Arizona" },
  { id: "America/Los_Angeles", label: "Pacific" },
  { id: "America/Anchorage", label: "Alaska" },
  { id: "Pacific/Honolulu", label: "Hawaii" },
] as const;

export const isUsTimeZone = (tz: string) => US_TIME_ZONES.some((z) => z.id === tz);

/**
 * One money-path row into a stored path, ported from Sonar's normalizeMoneyPath.
 * The editor shows the leading "/" itself, so a row may be typed with or
 * without one; a pasted URL keeps only its path. The query and hash go, one
 * trailing "/" goes, and anything with a space or over 200 characters is
 * rejected (never trimmed down into a different path). Case is kept.
 * An empty row is `""` (skipped); a bad one is `null`.
 */
export function moneyPathFromInput(text: string): string | null {
  let s = text.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try { s = new URL(s).pathname; } catch { return null; }
  }
  s = "/" + s.replace(/^\/+/, "");
  const cut = s.search(/[?#]/);
  if (cut !== -1) s = s.slice(0, cut);
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  if (/\s/.test(s) || s.length > MONEY_PATH_MAX_LENGTH) return null;
  return s;
}

export type PathIssue = "invalid" | "duplicate" | "limit";

/**
 * The money-path editor's rows into the list to save, with a per-row issue.
 * Duplicates are dropped (saved once) and only flagged; a bad path or one past
 * the 20-path cap blocks the save, so nothing the user typed is lost silently.
 */
export function moneyPathsFromRows(rows: string[]): { paths: string[]; issues: (PathIssue | null)[]; blocking: boolean } {
  const paths: string[] = [];
  const issues = rows.map((row): PathIssue | null => {
    const p = moneyPathFromInput(row);
    if (p === "") return null;
    if (p === null) return "invalid";
    if (paths.includes(p)) return "duplicate";
    if (paths.length >= MONEY_PATHS_MAX) return "limit";
    paths.push(p);
    return null;
  });
  return { paths, issues, blocking: issues.some((i) => i === "invalid" || i === "limit") };
}

/** A stored path as its editor row (the editor draws the leading "/"). */
export const moneyPathRow = (path: string) => path.replace(/^\//, "");

/**
 * The checked defaults plus the free-text extras ("quote_request, booking")
 * into one list, in that order, each name once. `invalid` names are the ones
 * the API would 400 on.
 */
export function conversionEventsFrom(checked: string[], extra: string): { events: string[]; invalid: string[]; overLimit: boolean } {
  const events: string[] = [];
  const invalid: string[] = [];
  for (const name of [...checked, ...extra.split(/[\s,]+/)]) {
    const n = name.trim();
    if (!n || events.includes(n)) continue;
    if (CONVERSION_EVENT_RE.test(n)) events.push(n);
    else if (!invalid.includes(n)) invalid.push(n);
  }
  return { events, invalid, overLimit: events.length > CONVERSION_EVENTS_MAX };
}

/** The fields that changed, for a PATCH that only sends those. */
export function settingsPatch(site: SiteSettings, next: Pick<SiteSettings, "label" | "time_zone" | "money_paths" | "conversion_events" | "monitor" | "monitor_url">): Partial<SiteSettings> {
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
  const patch: Partial<SiteSettings> = {};
  const label = next.label?.trim() || null;
  if (label !== (site.label || null)) patch.label = label;
  if (next.time_zone.trim() !== site.time_zone) patch.time_zone = next.time_zone.trim();
  if (!same(next.money_paths, site.money_paths)) patch.money_paths = next.money_paths;
  if (!same(next.conversion_events, site.conversion_events)) patch.conversion_events = next.conversion_events;
  if (next.monitor !== site.monitor) patch.monitor = next.monitor;
  const url = next.monitor_url?.trim() || null;
  if (url !== (site.monitor_url || null)) patch.monitor_url = url;
  return patch;
}

// ── Attribution and ROAS ──────────────────────────────────────────────────
// Ported from Sonar (src/lib/queries.ts Attribution/Roas, src/lib/format.ts).
// Money is integer cents everywhere; ratios are computed here, never stored.

export type AttributionModel = "first" | "last";

/** One (source, campaign) group, with BOTH models' numbers side by side. */
export type AttributionRow = {
  source: string | null;
  source_dim: "src" | "click_source" | null;
  campaign: string | null;
  /** True only on the unattributed bucket. */
  unattributed: boolean;
  filterable: boolean;
  first_conversions: number;
  first_revenue_cents: number;
  last_conversions: number;
  last_revenue_cents: number;
};

/** GET /api/web/:client_id/attribution — nola_web_attribution, unchanged. */
export type Attribution = {
  window_days: number;
  model: AttributionModel;
  conversions: number;
  revenue_cents: number;
  revenue_malformed: number;
  identified_conversions: number;
  attributed_conversions: number;
  unattributed_conversions: number;
  /** Distinct durable ids (visitor_id): People, never Visitors. */
  people: number;
  touchpoints: number;
  first_conversion_at: string | null;
  rows: AttributionRow[];
  rows_truncated: number;
  /** Always present, even at zero. */
  unattributed: AttributionRow;
  by_name: { name: string; conversions: number; revenue_cents: number }[];
};

export type CampaignNames = Record<string, Record<string, string>>;

export type RoasKind = "matched" | "spend_only" | "revenue_only";

export type RoasRow = {
  source: string | null;
  campaign: string | null;
  kind: RoasKind;
  source_dim: "src" | "click_source" | null;
  filterable: boolean;
  sites_with_traffic: number;
  spend_cents: number;
  impressions: number;
  clicks: number;
  first_conversions: number;
  first_revenue_cents: number;
  last_conversions: number;
  last_revenue_cents: number;
};

/** GET /api/web/:client_id/roas — nola_web_roas plus the untagged Google count and the suffix to fix it. */
export type Roas = {
  window_days: number;
  model: AttributionModel;
  from_date: string;
  to_date: string;
  spend_cents: number;
  impressions: number;
  clicks: number;
  spend_rows: number;
  matched_spend_cents: number;
  unmatched_spend_cents: number;
  other_site_spend_cents: number;
  other_site_campaigns: number;
  orphan_spend_cents: number;
  first_revenue_with_spend_cents: number;
  last_revenue_with_spend_cents: number;
  first_revenue_no_spend_cents: number;
  last_revenue_no_spend_cents: number;
  first_conversions_with_spend: number;
  last_conversions_with_spend: number;
  unattributed_revenue_cents: number;
  unattributed_conversions: number;
  total_revenue_cents: number;
  revenue_malformed: number;
  matched_campaigns: number;
  spend_only_campaigns: number;
  revenue_only_campaigns: number;
  spend_rows_total: number;
  last_spend_date: string | null;
  rows: RoasRow[];
  rows_truncated: number;
  /** Conversions credited to Google (gclid or utm_source google) with no utm_campaign: what the suffix fixes. */
  untagged_conversions: number;
  suffix_hint: string;
  /** source → campaign id → its latest synced name, for the spend campaigns in the rows. */
  campaign_names: CampaignNames;
};

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Integer cents → "$1,234.56". */
export function fmtMoneyCents(cents: number): string {
  return usd.format(Math.round(cents) / 100);
}

/** "4.50x", or "10.0x" once it reaches ten (rounded first), or "—" with no spend to divide by. */
export function fmtRoas(ratio: number | null): string {
  if (ratio === null) return "—";
  return Number(ratio.toFixed(2)) >= 10 ? `${ratio.toFixed(1)}x` : `${ratio.toFixed(2)}x`;
}

/** One row's ROAS under `model`: its credited revenue over its own spend. */
export function rowRoas(row: Pick<RoasRow, "spend_cents" | "first_revenue_cents" | "last_revenue_cents">, model: AttributionModel): number | null {
  return ratio(model === "first" ? row.first_revenue_cents : row.last_revenue_cents, row.spend_cents);
}

/**
 * The headline ROAS, as in Sonar: revenue from campaigns that HAVE spend, over
 * ALL spend in the window. Unmatched spend stays in the denominator, and
 * revenue with no spend row stays out of the numerator.
 */
export function blendedRoas(r: Pick<Roas, "spend_cents" | "first_revenue_with_spend_cents" | "last_revenue_with_spend_cents">, model: AttributionModel): number | null {
  return ratio(model === "first" ? r.first_revenue_with_spend_cents : r.last_revenue_with_spend_cents, r.spend_cents);
}

export const ATTRIBUTION_WINDOWS = [7, 30, 90] as const;
export type AttributionView = { model: AttributionModel; window: number };

/** `?model=&window=` from the page URL. Junk degrades to last click over 30 days. */
export function readAttribution(search: URLSearchParams): AttributionView {
  const model = search.get("model") === "first" ? "first" : "last";
  const w = Number(search.get("window"));
  return { model, window: (ATTRIBUTION_WINDOWS as readonly number[]).includes(w) ? w : 30 };
}

/** The attribution/roas query: the page's stats query plus the view's model and window. */
export function attributionQuery(statsQs: string, view: AttributionView): string {
  const q = new URLSearchParams(statsQs);
  q.set("model", view.model);
  q.set("window", String(view.window));
  return q.toString();
}

/** "google · Spring promo". A campaign id reads as its name when that source's spend sync knows it. */
export function attributionLabel(row: Pick<AttributionRow, "source" | "campaign" | "unattributed">, names: CampaignNames): string {
  if (row.unattributed) return "Direct / unattributed";
  const campaign = row.campaign === null ? null : (row.source !== null ? names[row.source]?.[row.campaign] : undefined) ?? row.campaign;
  return [row.source ?? "No source", campaign].filter(Boolean).join(" · ");
}

/**
 * The line a non-matched ROAS row gets. Spend with no traffic at all is a
 * broken destination or tag; spend with traffic is a page that did not
 * convert. They are different fixes, so they read differently.
 */
export function roasRowNote(row: Pick<RoasRow, "kind" | "sites_with_traffic">): string | null {
  if (row.kind === "revenue_only") return "Revenue only: no spend recorded for this campaign";
  if (row.kind === "spend_only") {
    return row.sites_with_traffic === 0
      ? "Spend only: no visitor arrived from this campaign on any tracked site. Check its final URL and tags."
      : "Spend only: visitors arrived but none converted";
  }
  return null;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** The callout's opening: how many Google conversions no spend can reach, with the pronoun to match. */
export function untaggedSummary(n: number): { headline: string; detail: string } {
  return {
    headline: `${fmtInt(n)} ${plural(n, "conversion", "conversions")} came from Google with no campaign tag.`,
    detail: `No ad spend can be matched to ${plural(n, "it", "them")}.`,
  };
}

// ── Uptime (Task 13) ──────────────────────────────────────────

export type UptimeKind = "up" | "down" | "blocked";
export type UptimeCheck = { checked_at: string; ok: boolean; status: number | null; ms: number | null; error: string | null; kind: UptimeKind };
export type UptimeIncident = { id: number; started_at: string; resolved_at: string | null; first_error: string | null };
/** GET /api/web/:client_id/uptime. `checks` (up to 48) and `incidents` (up to 10) are newest first. */
export type Uptime = {
  checks: UptimeCheck[];
  incidents: UptimeIncident[];
  state: "up" | "down" | "unknown";
  uptime_30d: number | null;
  monitor: boolean;
  url: string | null;
};
export type UptimeSlot = { at: string; kind: UptimeKind | "none"; check: UptimeCheck | null };

const HOUR_MS = 3_600_000;
const hourOf = (t: number) => Math.floor(t / HOUR_MS) * HOUR_MS;

/** The last 48 clock hours, oldest first, each with the check made in it (at most one per hour). */
export function uptimeHours(checks: UptimeCheck[], now = Date.now()): UptimeSlot[] {
  const byHour = new Map<number, UptimeCheck>();
  for (const c of checks) {
    const h = hourOf(Date.parse(c.checked_at));
    if (!byHour.has(h)) byHour.set(h, c);
  }
  const last = hourOf(now);
  return Array.from({ length: 48 }, (_, i) => {
    const h = last - (47 - i) * HOUR_MS;
    const check = byHour.get(h) ?? null;
    return { at: new Date(h).toISOString(), kind: check ? check.kind : "none", check };
  });
}

/** Sonar's checkLabel, with NOLA's badge tones. A check over 90 minutes old is overdue. */
export function uptimeStatus(u: Uptime, now = Date.now()): { label: string; tone: "" | "success" | "danger" | "warning" } {
  if (!u.monitor) return { label: "Monitoring off", tone: "" };
  if (u.state === "down") return { label: "Down", tone: "danger" };
  const latest = u.checks[0];
  if (!latest) return { label: "Awaiting first check", tone: "" };
  if (now - Date.parse(latest.checked_at) > 90 * 60_000) return { label: "Check overdue", tone: "warning" };
  if (latest.kind === "blocked") return { label: "Check blocked", tone: "warning" };
  return u.state === "up" ? { label: "Online", tone: "success" } : { label: "Unknown", tone: "" };
}

/** One decimal, rounded down, so a site that was ever down never reads 100%. */
export function fmtUptime(pct: number | null): string {
  if (pct === null) return "—";
  if (pct >= 100) return "100%";
  return `${(Math.floor(pct * 10) / 10).toFixed(1)}%`;
}

/** The same wording as the Slack recovery alert (utils/uptime.js fmtOutage). */
export function fmtOutage(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "less than a minute";
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  if (d) return h ? `${d} d ${h} h` : `${d} d`;
  if (h) return m ? `${h} h ${m} min` : `${h} h`;
  return `${m} min`;
}

// ── Websites overview (Task 14) ───────────────────────────────

/** One row of GET /api/web/overview. Every figure counts in the site's own zone, as its page does. */
export type OverviewEntry = {
  site: SiteSettings & { client_id: number };
  client: { id: number; name: string };
  visitors_7d: number;
  /** The 7 calendar days before those. */
  visitors_prev_7d: number;
  live: number;
  /** Exactly 7 daily visitor counts, oldest first. */
  spark: number[];
  uptime_state: Uptime["state"];
};

export type OverviewSort = "traffic" | "change" | "name";

export const siteName = (site: Pick<SiteSettings, "label" | "domains">) => site.label || site.domains[0] || "Untitled site";

/** Percent change on the week before, or null when the week before had no visitors. */
export function weekChange(current: number, previous: number): number | null {
  return previous > 0 ? ((current - previous) / previous) * 100 : null;
}

export function fmtChange(current: number, previous: number): { text: string; tone: "" | "success" | "danger" } {
  const pct = weekChange(current, previous);
  if (pct === null) return current > 0 ? { text: "New", tone: "success" } : { text: "—", tone: "" };
  const r = Math.round(pct);
  if (r === 0) return { text: "0%", tone: "" };
  return r > 0 ? { text: `+${r}%`, tone: "success" } : { text: `−${-r}%`, tone: "danger" };
}

// For the "change" sort: a site with visitors and none the week before ranks
// above any percentage; one with neither ranks last.
const changeRank = (e: OverviewEntry) => weekChange(e.visitors_7d, e.visitors_prev_7d) ?? (e.visitors_7d > 0 ? Infinity : -Infinity);

/** Down sites always first; then the chosen order. Returns a new array. */
export function sortOverview(entries: OverviewEntry[], sort: OverviewSort): OverviewEntry[] {
  const byName = (a: OverviewEntry, b: OverviewEntry) =>
    a.client.name.localeCompare(b.client.name) || siteName(a.site).localeCompare(siteName(b.site));
  const order = {
    traffic: (a: OverviewEntry, b: OverviewEntry) => b.visitors_7d - a.visitors_7d || b.live - a.live || byName(a, b),
    change: (a: OverviewEntry, b: OverviewEntry) => {
      const ra = changeRank(a), rb = changeRank(b);
      return (ra === rb ? 0 : rb > ra ? 1 : -1) || b.visitors_7d - a.visitors_7d || byName(a, b);
    },
    name: byName,
  }[sort];
  const down = (e: OverviewEntry) => (e.uptime_state === "down" ? 0 : 1);
  return [...entries].sort((a, b) => down(a) - down(b) || order(a, b));
}

/** The card's uptime badge. The overview has no check history, so "unknown" is one plain label. */
export function overviewUptime(e: OverviewEntry): { label: string; tone: "" | "success" | "danger" } {
  if (!e.site.monitor) return { label: "Monitoring off", tone: "" };
  if (e.uptime_state === "down") return { label: "Down", tone: "danger" };
  if (e.uptime_state === "up") return { label: "Online", tone: "success" };
  return { label: "No recent check", tone: "" };
}

/** Polyline points for a sparkline `width` × `height`, with 2px of room top and bottom. */
export function sparkPoints(values: number[], width: number, height: number): string {
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, values.length - 1);
  return values.map((v, i) => `${Math.round(i * step * 10) / 10},${Math.round((height - 2 - (v / max) * (height - 4)) * 10) / 10}`).join(" ");
}
